import React, { useCallback, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import stylex from '@stylexjs/stylex';
import { styles } from '../styles';
import '../styles.css';

const OVERSCAN = 5;

interface VirtualizedParticipantsListProps<T extends { id: number }> {
  readonly items: readonly T[];
  readonly renderItem: (item: T) => React.ReactNode;
  readonly itemHeight?: number;
  readonly height?: number;
  readonly scrollResetKey?: string | number;
}

export function VirtualizedParticipantsList<T extends { id: number }>({
  items,
  renderItem,
  itemHeight = 130,
  height = 600,
  scrollResetKey,
}: VirtualizedParticipantsListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);

  const getItemKey = useCallback((index: number) => items[index]?.id ?? index, [items]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan: OVERSCAN,
    getItemKey,
  });

  useEffect(() => {
    if (scrollResetKey === undefined) {
      return;
    }

    if (parentRef.current) {
      parentRef.current.scrollTop = 0;
    }
  }, [scrollResetKey]);

  if (!items.length) return null;

  return (
    <div
      ref={parentRef}
      {...stylex.props(styles.virtualScrollContainer)}
      data-virtual-scroll
      style={{ height: `${height}px` }}
    >
      <div {...stylex.props(styles.virtualInnerContainer)} style={{ height: `${virtualizer.getTotalSize()}px` }}>
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = items[virtualRow.index];

          return (
            <div
              key={virtualRow.key}
              ref={virtualizer.measureElement}
              data-index={virtualRow.index}
              {...stylex.props(styles.virtualRow)}
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              {renderItem(item)}
            </div>
          );
        })}
      </div>
    </div>
  );
}
